import React, { useState } from 'react';
import {
  User,
  AtSign,
  Mail,
  Lock,
  Pencil,
  Check,
  X,
  KeyRound,
  Trash2,
  Eye,
  EyeOff,
  ShieldAlert,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useToastStore } from '../hooks/useToast';
import { ApiError } from '../api/client';
import { Button } from '../components/ui/Button';
import { Card, CardHeader, CardTitle } from '../components/ui/Card';
import { ConfirmModal } from '../components/ui/ConfirmModal';
import { Avatar } from '../components/ui/Avatar';
import SEOHead from '../components/SEOHead';
import { cn } from '../utils/cn';

const MIN_PASSWORD_LENGTH = 8;

interface FieldProps {
  label: string;
  icon: React.ReactNode;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  placeholder?: string;
  error?: string;
}

const Field: React.FC<FieldProps> = ({
  label,
  icon,
  value,
  onChange,
  type = 'text',
  placeholder,
  error,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const isPassword = type === 'password';

  return (
    <div>
      <label className="text-xs font-medium text-theme-secondary mb-1.5 block">{label}</label>
      <div className="relative group">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-theme-muted group-hover:text-brand transition-colors">
          {icon}
        </span>
        <input
          type={isPassword && showPassword ? 'text' : type}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={cn(
            'w-full py-2.5 rounded-theme-md border-2 outline-none transition-colors text-sm',
            'bg-theme-tertiary text-theme-primary placeholder:text-theme-muted',
            error ? 'border-danger' : 'border-theme-hover focus:border-brand',
            isPassword ? 'pl-10 pr-10' : 'pl-10 pr-3',
          )}
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            tabIndex={-1}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-theme-muted hover:text-brand transition-colors"
          >
            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        )}
      </div>
      {error && <p className="text-danger text-xs mt-1.5 pl-10">{error}</p>}
    </div>
  );
};

interface InfoRowProps {
  icon: React.ReactNode;
  label: string;
  value: string;
}

const InfoRow: React.FC<InfoRowProps> = ({ icon, label, value }) => (
  <div className="group flex items-center gap-3 py-3 border-b border-theme last:border-b-0">
    <span className="flex items-center justify-center w-9 h-9 rounded-theme-full bg-theme-tertiary text-theme-muted group-hover:text-brand transition-colors shrink-0">
      {icon}
    </span>
    <div className="min-w-0">
      <p className="text-xs text-theme-secondary">{label}</p>
      <p className="text-theme-primary font-medium truncate">{value || '—'}</p>
    </div>
  </div>
);

const ProfileSettingsPage: React.FC = () => {
  const { user, isActivated, updateProfile, changePassword, deleteAccount, resendVerification } =
    useAuth();
  const navigate = useNavigate();
  const showToast = useToastStore((s) => s.showToast);

  const [isEditing, setIsEditing] = useState(false);
  const [username, setUsername] = useState(user?.username ?? '');
  const [firstName, setFirstName] = useState(user?.first_name ?? '');
  const [lastName, setLastName] = useState(user?.second_name ?? '');

  const [profileErrors, setProfileErrors] = useState<Record<string, string>>({});
  const [profileFormError, setProfileFormError] = useState('');
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isResending, setIsResending] = useState(false);

  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [passwordErrors, setPasswordErrors] = useState<Record<string, string>>({});
  const [passwordFormError, setPasswordFormError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const startEditing = () => {
    setUsername(user?.username ?? '');
    setFirstName(user?.first_name ?? '');
    setLastName(user?.second_name ?? '');
    setProfileErrors({});
    setProfileFormError('');
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setProfileErrors({});
    setProfileFormError('');
  };

  const validateProfile = (): boolean => {
    const errors: Record<string, string> = {};
    if (!username.trim()) {
      errors.username = 'Введите имя пользователя';
    }
    setProfileErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfileFormError('');

    if (!validateProfile()) {
      return;
    }

    setIsSavingProfile(true);
    try {
      await updateProfile({ username, firstName, lastName });
      setIsEditing(false);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'conflict' && err.message.toLowerCase().includes('username')) {
          setProfileErrors((prev) => ({ ...prev, username: 'Это имя пользователя уже занято' }));
        } else {
          setProfileFormError(err.message);
        }
      } else {
        setProfileFormError('Не удалось сохранить изменения. Попробуйте позже.');
      }
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleResendVerification = async () => {
    if (isResending) return;
    setIsResending(true);
    try {
      await resendVerification();
      showToast('Письмо подтверждения отправлено', 'success');
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Не удалось отправить письмо. Попробуйте позже.';
      showToast(message, 'error');
    } finally {
      setIsResending(false);
    }
  };

  const startChangingPassword = () => {
    setOldPassword('');
    setNewPassword('');
    setConfirmNewPassword('');
    setPasswordErrors({});
    setPasswordFormError('');
    setPasswordSuccess('');
    setIsChangingPassword(true);
  };

  const cancelChangingPassword = () => {
    setIsChangingPassword(false);
    setPasswordErrors({});
    setPasswordFormError('');
  };

  const validatePassword = (): boolean => {
    const errors: Record<string, string> = {};
    if (!oldPassword) {
      errors.oldPassword = 'Введите текущий пароль';
    }
    if (newPassword.length < MIN_PASSWORD_LENGTH) {
      errors.newPassword = `Пароль должен быть не короче ${MIN_PASSWORD_LENGTH} символов`;
    }
    if (confirmNewPassword !== newPassword) {
      errors.confirmNewPassword = 'Пароли не совпадают';
    }
    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordFormError('');
    setPasswordSuccess('');

    if (!validatePassword()) {
      return;
    }

    setIsSavingPassword(true);
    try {
      await changePassword(oldPassword, newPassword);
      setPasswordSuccess('Пароль успешно изменён');
      setIsChangingPassword(false);
    } catch (err) {
      if (err instanceof ApiError) {
        setPasswordFormError(err.message);
      } else {
        setPasswordFormError('Не удалось изменить пароль. Попробуйте позже.');
      }
    } finally {
      setIsSavingPassword(false);
    }
  };

  const openDeleteModal = () => {
    setDeleteError('');
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setDeleteError('');
  };

  const handleDeleteAccount = async () => {
    setDeleteError('');
    setIsDeleting(true);
    try {
      await deleteAccount();
      navigate('/login');
    } catch (err) {
      if (err instanceof ApiError) {
        setDeleteError(err.message);
      } else if (err instanceof Error) {
        setDeleteError(err.message);
      } else {
        setDeleteError('Не удалось удалить аккаунт. Попробуйте позже.');
      }
      setIsDeleting(false);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-0 space-y-5">
      <SEOHead
        title={user?.username ? `Настройки — ${user.username}` : 'Настройки профиля'}
        description="Редактирование личных данных, смена пароля и управление аккаунтом."
      />
      {/* Предупреждение о неподтверждённой почте */}
      {!isActivated && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-theme-md border border-warning/30 bg-warning/10 p-4">
          <ShieldAlert size={20} className="text-warning shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-theme-primary">Email не подтверждён</p>
            <p className="text-xs text-theme-secondary mt-0.5">
              Подтвердите адрес {user.email}, чтобы получить доступ ко всем функциям.
            </p>
          </div>
          <Button
            size="sm"
            variant="secondary"
            onClick={handleResendVerification}
            disabled={isResending}
            className="shrink-0"
          >
            {isResending ? 'Отправка...' : 'Отправить письмо'}
          </Button>
        </div>
      )}
      {/* Hero-блок профиля */}
      <Card className="relative overflow-hidden p-0">
        <div className="h-20 sm:h-24 bg-gradient-to-r from-brand to-brand-dark" />
        <div className="px-4 sm:px-6 pb-5 sm:pb-6 -mt-10 sm:-mt-12 flex flex-col sm:flex-row sm:items-end gap-4">
          <Avatar
            username={user.username}
            fallbackClassName="text-3xl"
            className="w-20 h-20 sm:w-24 sm:h-24 border-4 border-theme-secondary shadow-theme-card"
          />
          <div className="min-w-0 flex-1">
            <h1 className="text-lg sm:text-xl font-semibold text-theme-primary truncate">
              {user.first_name || user.second_name
                ? `${user.first_name ?? ''} ${user.second_name ?? ''}`.trim()
                : user.username}
            </h1>
            <p className="text-sm text-theme-secondary truncate">@{user.username}</p>
          </div>
          {!isEditing && (
            <Button
              variant="secondary"
              size="sm"
              onClick={startEditing}
              className="flex items-center gap-1.5 self-start sm:self-auto"
            >
              <Pencil size={14} /> Редактировать
            </Button>
          )}
        </div>
      </Card>

      {/* Личные данные */}
      <Card>
        <CardHeader>
          <CardTitle>Личные данные</CardTitle>
        </CardHeader>

        {!isEditing ? (
          <div>
            <InfoRow icon={<User size={16} />} label="Имя" value={user.first_name ?? ''} />
            <InfoRow icon={<User size={16} />} label="Фамилия" value={user.second_name ?? ''} />
            <InfoRow icon={<AtSign size={16} />} label="Имя пользователя" value={user.username} />
            <InfoRow icon={<Mail size={16} />} label="Email" value={user.email} />
          </div>
        ) : (
          <form onSubmit={handleProfileSubmit} className="flex flex-col gap-4" noValidate>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field
                label="Имя"
                icon={<User size={16} />}
                value={firstName}
                onChange={setFirstName}
              />
              <Field
                label="Фамилия"
                icon={<User size={16} />}
                value={lastName}
                onChange={setLastName}
              />
            </div>

            <Field
              label="Имя пользователя"
              icon={<AtSign size={16} />}
              value={username}
              onChange={setUsername}
              error={profileErrors.username}
            />

            {/* Email — неизменяемый идентификатор. Показываем как InfoRow,
                чтобы пользователь видел свой адрес, но не мог его редактировать. */}
            <InfoRow icon={<Mail size={16} />} label="Email" value={user.email} />
            <p className="-mt-2 pl-1 text-xs text-theme-muted">
              Email является вашим идентификатором и не может быть изменён.
            </p>

            {profileFormError && <p className="text-danger text-sm">{profileFormError}</p>}

            <div className="flex flex-col sm:flex-row gap-3 mt-1">
              <Button
                type="button"
                variant="secondary"
                onClick={cancelEditing}
                disabled={isSavingProfile}
                className="flex-1 flex items-center justify-center gap-1.5"
              >
                <X size={16} /> Отмена
              </Button>
              <Button
                type="submit"
                disabled={isSavingProfile}
                className="flex-1 flex items-center justify-center gap-1.5"
              >
                <Check size={16} /> {isSavingProfile ? 'Сохранение...' : 'Сохранить'}
              </Button>
            </div>
          </form>
        )}
      </Card>

      {/* Пароль */}
      <Card>
        <CardHeader>
          <CardTitle>Безопасность</CardTitle>
          {!isChangingPassword && (
            <Button
              variant="secondary"
              size="sm"
              onClick={startChangingPassword}
              className="flex items-center gap-1.5"
            >
              <KeyRound size={14} /> Изменить пароль
            </Button>
          )}
        </CardHeader>

        {!isChangingPassword ? (
          <InfoRow icon={<Lock size={16} />} label="Пароль" value={passwordSuccess || '••••••••'} />
        ) : (
          <form onSubmit={handlePasswordSubmit} className="flex flex-col gap-4" noValidate>
            <Field
              label="Текущий пароль"
              icon={<Lock size={16} />}
              type="password"
              value={oldPassword}
              onChange={setOldPassword}
              error={passwordErrors.oldPassword}
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field
                label="Новый пароль"
                icon={<Lock size={16} />}
                type="password"
                value={newPassword}
                onChange={setNewPassword}
                error={passwordErrors.newPassword}
              />
              <Field
                label="Повторите новый пароль"
                icon={<Lock size={16} />}
                type="password"
                value={confirmNewPassword}
                onChange={setConfirmNewPassword}
                error={passwordErrors.confirmNewPassword}
              />
            </div>

            {passwordFormError && <p className="text-danger text-sm">{passwordFormError}</p>}

            <div className="flex flex-col sm:flex-row gap-3 mt-1">
              <Button
                type="button"
                variant="secondary"
                onClick={cancelChangingPassword}
                disabled={isSavingPassword}
                className="flex-1 flex items-center justify-center gap-1.5"
              >
                <X size={16} /> Отмена
              </Button>
              <Button
                type="submit"
                disabled={isSavingPassword}
                className="flex-1 flex items-center justify-center gap-1.5"
              >
                <Check size={16} /> {isSavingPassword ? 'Сохранение...' : 'Изменить пароль'}
              </Button>
            </div>
          </form>
        )}
      </Card>

      {/* Удаление аккаунта*/}
      <Card>
        <CardHeader>
          <CardTitle className="text-danger">Удаление аккаунта</CardTitle>
          <Button
            variant="danger"
            size="sm"
            onClick={openDeleteModal}
            className="flex items-center gap-1.5"
          >
            <Trash2 size={14} /> Удалить аккаунт
          </Button>
        </CardHeader>
        <div className="border border-danger/20 rounded-theme-md p-4 bg-danger-light/10 mx-4 sm:mx-6 mb-5 sm:mb-6">
          <p className="text-sm text-theme-secondary">
            Удаление аккаунта приведёт к безвозвратному удалению всех ваших файлов, директорий и
            данных.
            <br />
            Это действие нельзя отменить.
          </p>
        </div>
      </Card>

      {/* Модальное окно подтверждения */}
      <ConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDeleteAccount}
        variant="danger"
        confirmLabel="Удалить"
        cancelLabel="Отмена"
        showCancel={true}
        isConfirming={isDeleting}
        error={deleteError}
        title={
          <div>
            <h3 className="text-lg font-semibold text-theme-primary">Удаление аккаунта</h3>
            <p className="text-sm text-theme-muted">Это действие нельзя будет отменить</p>
          </div>
        }
        description={
          <p className="text-sm text-theme-secondary mt-2">
            Вы уверены, что хотите полностью удалить свой аккаунт?
            <br />
            Все ваши файлы, директории и данные будут безвозвратно удалены.
          </p>
        }
      />
    </div>
  );
};

export default ProfileSettingsPage;
